import { Loading } from '@/components/loading';
import { Body2, Body3 } from '@/components/typography/body/bodies.component';
import { cn } from '@/lib/utils';
import { useSoilPhData } from '@/modules/Report/Report.api';
import { toast } from 'react-toastify';

const RainbowScale = ({ position }: { position: number }) => {
  return (
    <>
      <div id="1" className={cn('relative w-full')}>
        {/* PH Scale */}
        <div id="2" className={cn('relative flex flex-row')}>
          <div className={cn('w-full border-y border-l h-8 bg-phScale-1')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-2')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-3')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-4')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-5')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-6')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-7')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-8')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-9')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-10')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-11')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-12')} />
          <div className={cn('w-full border-y border-l h-8 bg-phScale-13')} />
          <div className={cn('w-full border h-8 bg-phScale-14')} />
        </div>
        {/* Chart pH Marker */}
        <div
          id="3"
          className={cn('absolute w-5 h-5 rounded-full bg-neutral-0')}
          style={{ left: `${position}%`, transform: 'translateX(-50%)', top: '10%' }}
        />
        {/* X-axis labels */}
        <div className={cn('flex flex-row w-full')}>
          <div className={cn('flex flex-col w-1/2')}>
            <div className={cn('w-full border-l h-2')} />
            <Body2 className="-ml-1">3</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>4</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>5</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>6</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>7</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>8</Body2>
          </div>
          <div className={cn('flex flex-col w-full items-center')}>
            <div className={cn('flex flex-row w-full')}>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn('border-l h-2')} />
              </div>
              <div className={cn('flex flex-col w-full')}>
                <div className={cn(' border-l h-2')} />
              </div>
            </div>
            <Body2>9</Body2>
          </div>
          <div className={cn('flex flex-row w-1/2')}>
            <div className={cn('flex flex-col w-full')}>
              <div className={cn('border-x h-2')} />
            </div>
            <div className={cn('flex flex-col w-0')}>
              <div className={cn('h-2')} />
              <Body2 className={cn(' h-2 -ml-1.5')}>10</Body2>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const SoilPhChart = ({ fieldId }: { fieldId: string }) => {
  const { data: phData, status } = useSoilPhData(fieldId);
  if (status === 'pending') return <Loading />;
  if (status === 'error') {
    toast.error('Failed to fetch pH data');
    return;
  }

  const pH = Number(phData.soilPh);
  const position = ((pH - 3) / (10 - 3)) * 100;

  return (
    <div>
      <Body2 className="mb-3">
        Soil pH: <b>{pH}</b>
      </Body2>
      <RainbowScale position={position} />
      <div className={cn('flex flex-row w-full mt-5')}>
        <div className="w-full">
          <Body2 className="font-semibold">Increasing Acidity</Body2>
          <Body3>Fungi thrive </Body3>
          <Body3> Bacterial activity declines </Body3>
          <Body3> Nutrient cycling drops</Body3>
        </div>
        <div className="w-full">
          <Body2 className={cn('font-semibold text-center')}>Neutral</Body2>
          <Body3 className="text-center">Desirable fungal and bacterial activity</Body3>
          <Body3 className="text-center">Good earthworm activity</Body3>
          <Body3 className="text-center">Nutrient cycling thrives</Body3>
        </div>
        <div className="w-full">
          <Body2 className={cn('font-semibold text-right')}>Increasing Alkalinity</Body2>
          <Body3 className="text-right">Fungal activity declines</Body3>
          <Body3 className="text-right">Bacteria thrive</Body3>
          <Body3 className="text-right"> Nutrient cycling drops</Body3>
        </div>
      </div>
    </div>
  );
};
